import { CopyRoomFromProjectDto } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getBuildingsQueryKey } from '@/features/buildings/api/get-buildings';
import { api } from '@/lib/api-client';

import { CopyRoomJobResponse } from './copy-items-from-room';
import { getItemsQueryKey } from './get-items';

export type { CopyRoomFromProjectDto };

const copyRoomFromProjectRequest = async (
  orgId: string,
  projectId: string,
  buildingId: string,
  floorId: string,
  data: CopyRoomFromProjectDto,
): Promise<CopyRoomJobResponse> => {
  const { data: result } = await api.post<unknown, ApiResponse<CopyRoomJobResponse>>(
    `/orgs/${orgId}/projects/${projectId}/buildings/${buildingId}/floors/${floorId}/copy-room-from-project`,
    data,
  );
  return result;
};

interface UseCopyRoomFromProjectOptions {
  mutationConfig?: MutationConfig<
    (args: {
      buildingId: string;
      floorId: string;
      dto: CopyRoomFromProjectDto;
    }) => Promise<CopyRoomJobResponse>
  >;
}

export const useCopyRoomFromProject = ({ mutationConfig }: UseCopyRoomFromProjectOptions = {}) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId, projectId } = useScopeIds('org', 'project');
  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    onSuccess: (...args) => {
      // Building tree refreshes once the new room lands via stream, but kicking
      // the invalidation off optimistically lets the floor-picker show it as
      // soon as React Query refetches. Items invalidation follows the same
      // shape as the room-target endpoint.
      void queryClient.invalidateQueries({ queryKey: getBuildingsQueryKey(orgId, projectId) });
      void queryClient.invalidateQueries({ queryKey: getItemsQueryKey(orgId, projectId) });
      void queryClient.invalidateQueries({ queryKey: ['zones'] });
      // Skip the default toast when the caller supplies its own onSuccess.
      if (!onSuccess) {
        notifications.success('items.copyRoomFromProject.queued');
      } else {
        onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (!onError) {
        notifications.error('items.copyRoomFromProject.error');
      } else {
        onError(...args);
      }
    },
    ...restConfig,
    mutationFn: ({
      buildingId,
      floorId,
      dto,
    }: {
      buildingId: string;
      floorId: string;
      dto: CopyRoomFromProjectDto;
    }) => copyRoomFromProjectRequest(orgId, projectId, buildingId, floorId, dto),
  });
};
